import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useAuth } from '../stores/auth';
import api from '../lib/api';
import { connectSocket, getSocket } from '../lib/socket';
import { ArrowLeft, SendHorizontal, Users } from 'lucide-react';

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_name: string;
  content: string;
  created_at: string;
}

interface ConversationDetail {
  id: string;
  type: 'DIRECT' | 'GROUP';
  name: string | null;
  participants: {
    user_id: string;
    user_name: string;
    role: string;
    online?: boolean;
  }[];
}

interface ChatRoomProps {
  conversationId: string;
}

function formatMessageTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatDateSeparator(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Hoje';
  if (date.toDateString() === yesterday.toDateString()) return 'Ontem';

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-red-500',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function ChatRoom({ conversationId }: ChatRoomProps) {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [messageText, setMessageText] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch conversation details
  const { data: conversation } = useQuery<ConversationDetail>({
    queryKey: ['conversation', conversationId],
    queryFn: () =>
      api.get(`/chat/conversations/${conversationId}`).then((res) => res.data.data),
  });

  // Fetch initial messages
  const { data: initialMessages, isLoading: messagesLoading } = useQuery<Message[]>({
    queryKey: ['messages', conversationId],
    queryFn: () =>
      api
        .get(`/chat/conversations/${conversationId}/messages`)
        .then((res) => res.data.data),
  });

  // Set initial messages
  useEffect(() => {
    if (initialMessages) {
      setMessages(initialMessages);
    }
  }, [initialMessages]);

  // Scroll to bottom on initial load and new messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Socket connection and events
  useEffect(() => {
    try {
      connectSocket();
    } catch {
      // No token
    }

    const socket = getSocket();
    if (!socket) return;

    // Join conversation room
    socket.emit('join_conversation', { conversationId });

    // Mark as read
    socket.emit('mark_read', { conversationId });

    // Also mark read via REST as fallback
    api.post(`/chat/conversations/${conversationId}/read`).catch(() => {});

    const handleNewMessage = (message: Message) => {
      if (message.conversation_id === conversationId) {
        setMessages((prev) => {
          // Avoid duplicates
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
        // Mark as read since we're in the room
        socket.emit('mark_read', { conversationId });
      }
    };

    const handleUserTyping = (data: { conversationId: string; userName: string; userId: string }) => {
      if (data.conversationId === conversationId && data.userId !== user?.id) {
        setTypingUser(data.userName);
        // Clear typing after 3 seconds
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
      }
    };

    socket.on('new_message', handleNewMessage);
    socket.on('user_typing', handleUserTyping);

    return () => {
      socket.emit('leave_conversation', { conversationId });
      socket.off('new_message', handleNewMessage);
      socket.off('user_typing', handleUserTyping);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [conversationId, user?.id]);

  // Invalidate unread count when entering the room
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['chat-unread'] });
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
  }, [conversationId, queryClient]);

  // Send message
  const handleSend = async () => {
    const content = messageText.trim();
    if (!content) return;

    setMessageText('');
    inputRef.current?.focus();

    const socket = getSocket();

    // Optimistic message
    const optimisticMsg: Message = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId,
      sender_id: user?.id || '',
      sender_name: user?.name || '',
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticMsg]);

    if (socket?.connected) {
      socket.emit('send_message', { conversationId, content }, (ack: { success: boolean; data?: Message }) => {
        if (ack?.success && ack.data) {
          // Replace optimistic message with server response
          setMessages((prev) =>
            prev.map((m) => (m.id === optimisticMsg.id ? ack.data! : m))
          );
        }
      });
    } else {
      // REST fallback
      try {
        const res = await api.post(`/chat/conversations/${conversationId}/messages`, { content });
        const serverMsg = res.data.data;
        setMessages((prev) =>
          prev.map((m) => (m.id === optimisticMsg.id ? serverMsg : m))
        );
      } catch {
        // Remove optimistic message on failure
        setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id));
      }
    }
  };

  // Emit typing event
  const handleInputChange = (value: string) => {
    setMessageText(value);
    const socket = getSocket();
    if (socket?.connected) {
      socket.emit('typing', { conversationId });
    }
  };

  // Load older messages on scroll to top
  const handleScroll = async () => {
    const container = messagesContainerRef.current;
    if (!container || loadingOlder || !hasMore) return;

    if (container.scrollTop < 50 && messages.length > 0) {
      setLoadingOlder(true);
      const oldestMsg = messages[0];

      try {
        const res = await api.get(
          `/chat/conversations/${conversationId}/messages?before=${oldestMsg.id}`
        );
        const olderMessages: Message[] = res.data.data;

        if (olderMessages.length === 0) {
          setHasMore(false);
        } else {
          const prevScrollHeight = container.scrollHeight;
          setMessages((prev) => [...olderMessages, ...prev]);
          // Maintain scroll position after prepending
          requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight - prevScrollHeight;
          });
        }
      } catch {
        // Ignore pagination errors
      } finally {
        setLoadingOlder(false);
      }
    }
  };

  // Conversation display name
  const getDisplayName = (): string => {
    if (conversation?.type === 'GROUP' && conversation.name) return conversation.name;
    const other = conversation?.participants.find((p) => p.user_id !== user?.id);
    return other?.user_name || 'Chat';
  };

  const getOnlineStatus = (): boolean => {
    if (!conversation) return false;
    const other = conversation.participants.find((p) => p.user_id !== user?.id);
    return other?.online ?? false;
  };

  // Group messages by date for separators
  const getDateKey = (dateStr: string): string => new Date(dateStr).toDateString();

  let lastDateKey = '';

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 safe-area-top">
        <button
          onClick={() => setLocation('/conversations')}
          className="p-1.5 -ml-1.5 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="h-6 w-6 text-gray-700" />
        </button>

        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Avatar */}
          <div
            className={`flex-shrink-0 w-10 h-10 rounded-full ${getAvatarColor(getDisplayName())} flex items-center justify-center`}
          >
            {conversation?.type === 'GROUP' ? (
              <Users className="h-4 w-4 text-white" />
            ) : (
              <span className="text-xs font-bold text-white">
                {getInitials(getDisplayName())}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-gray-900 truncate">{getDisplayName()}</h1>
            {typingUser ? (
              <p className="text-xs text-indigo-600 font-medium">digitando...</p>
            ) : getOnlineStatus() ? (
              <p className="text-xs text-green-600">Online</p>
            ) : (
              <p className="text-xs text-gray-400">
                {conversation?.type === 'GROUP'
                  ? `${conversation.participants.length} participantes`
                  : 'Offline'}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-3"
      >
        {loadingOlder && (
          <div className="flex justify-center py-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        )}

        {messagesLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" />
              <p className="text-gray-500 text-sm">Carregando mensagens...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <MessageBubbleIcon />
            <p className="text-sm text-gray-500 mt-3">Nenhuma mensagem ainda</p>
            <p className="text-xs text-gray-400 mt-1">Envie a primeira mensagem!</p>
          </div>
        ) : (
          <>
            {messages.map((msg) => {
              const isMine = msg.sender_id === user?.id;
              const dateKey = getDateKey(msg.created_at);
              const showDateSeparator = dateKey !== lastDateKey;
              lastDateKey = dateKey;

              return (
                <div key={msg.id}>
                  {/* Date separator */}
                  {showDateSeparator && (
                    <div className="flex justify-center my-4">
                      <span className="text-[11px] text-gray-500 bg-gray-200 px-3 py-1 rounded-full">
                        {formatDateSeparator(msg.created_at)}
                      </span>
                    </div>
                  )}

                  {/* Message bubble */}
                  <div className={`flex mb-2 ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        isMine
                          ? 'bg-indigo-600 text-white rounded-br-md'
                          : 'bg-white text-gray-900 rounded-bl-md shadow-sm'
                      }`}
                    >
                      {/* Sender name for group chats */}
                      {!isMine && conversation?.type === 'GROUP' && (
                        <p className="text-[11px] font-semibold text-indigo-600 mb-0.5">
                          {msg.sender_name}
                        </p>
                      )}

                      <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {msg.content}
                      </p>

                      <p
                        className={`text-[10px] mt-1 ${
                          isMine ? 'text-white/70' : 'text-gray-400'
                        } text-right`}
                      >
                        {formatMessageTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* Typing indicator */}
        {typingUser && (
          <div className="flex justify-start mb-2">
            <div className="bg-white rounded-2xl rounded-bl-md px-4 py-2 shadow-sm">
              <div className="flex items-center gap-1">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 safe-area-bottom">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            placeholder="Digite sua mensagem..."
            value={messageText}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className="flex-1 px-4 py-2.5 rounded-full bg-gray-100 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={handleSend}
            disabled={!messageText.trim()}
            className="p-2.5 rounded-full bg-indigo-600 text-white active:bg-indigo-700 disabled:opacity-40 disabled:active:bg-indigo-600 transition-colors"
          >
            <SendHorizontal className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubbleIcon() {
  return (
    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
      <SendHorizontal className="h-7 w-7 text-gray-400" />
    </div>
  );
}
